/**
 * Tab selection state for {@link MiniModal} tabbed layouts.
 * Supports controlled and uncontrolled modes, optional change notifications, and haptics.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { triggerSelectionHaptic } from "../../tools/haptics.tools";

/** Minimal tab identity required by the tabs hook. */
export interface MiniModalTabIdentity {

    /** Stable tab identifier. */
    "id": string;
}

interface UseMiniModalTabsOptions<TTab extends MiniModalTabIdentity> {

    /** Ordered tab definitions (at least one). */
    "tabs": readonly TTab[];

    /** Controlled active tab id. When set, internal state is not updated on change. */
    "activeTabId"?: string;

    /** Initial tab id when uncontrolled. Falls back to the first tab. */
    "defaultTabId"?: string;

    /** Notified whenever the active tab changes (controlled or uncontrolled). */
    "onActiveTabIdChange"?: ( tabId: string ) => void;

    /** When `true`, fires a light haptic on tab change after mount (native only). Defaults to `true`. */
    "enableTabHaptics"?: boolean;
}

interface UseMiniModalTabsResult {

    /** Resolved active tab id (controlled value or internal state). */
    "activeTabId": string;

    /** Updates the active tab and runs side effects (haptic, external notifier). */
    "setActiveTabId": ( tabId: string ) => void;
}

/**
 * Resolves the first valid tab id from a tab list.
 *
 * @param tabs Tab definitions.
 * @param preferredId Candidate id to prefer when present in the list.
 * @returns A valid tab id, or an empty string when the list is empty.
 */
const resolveInitialTabId = <TTab extends MiniModalTabIdentity>(
    tabs: readonly TTab[],
    preferredId?: string,
): string => {
    if ( tabs.length === 0 ) {
        return "";
    }

    if ( preferredId !== undefined && tabs.some( ( tab ) => tab.id === preferredId ) ) {
        return preferredId;
    }

    return tabs[0].id;
};

/**
 * Manages MiniModal tab selection with controlled/uncontrolled semantics.
 *
 * @param options Tab list and optional controlled id / callbacks.
 * @returns Active tab id and setter used by {@link MiniModal} and {@link MiniModalTabs}.
 */
export const useMiniModalTabs = <TTab extends MiniModalTabIdentity>(
    options: UseMiniModalTabsOptions<TTab>,
): UseMiniModalTabsResult => {
    const {
        tabs,
        "activeTabId": controlledActiveTabId,
        defaultTabId,
        onActiveTabIdChange,
        enableTabHaptics = true,
    } = options;

    const isControlled = controlledActiveTabId !== undefined;
    const [ uncontrolledTabId, setUncontrolledTabId ] = useState<string>( () => {
        return resolveInitialTabId( tabs, defaultTabId );
    } );
    const hasMountedRef = useRef<boolean>( false );
    const previousTabIdRef = useRef<string>( controlledActiveTabId ?? uncontrolledTabId );

    const activeTabId = isControlled
        ? resolveInitialTabId( tabs, controlledActiveTabId )
        : resolveInitialTabId( tabs, uncontrolledTabId );

    useEffect( () => {
        previousTabIdRef.current = activeTabId;
    }, [ activeTabId ] );

    useEffect( () => {
        hasMountedRef.current = true;
    }, [] );

    /**
     * Applies a tab change: updates internal state when uncontrolled, notifies parent, optional haptic.
     *
     * @param nextTabId Newly selected tab id.
     */
    const setActiveTabId = useCallback( ( nextTabId: string ): void => {
        if ( tabs.some( ( tab ) => tab.id === nextTabId ) === false ) {
            return;
        }

        if ( nextTabId === previousTabIdRef.current ) {
            return;
        }

        previousTabIdRef.current = nextTabId;

        if ( isControlled === false ) {
            setUncontrolledTabId( nextTabId );
        }

        onActiveTabIdChange?.( nextTabId );

        if ( hasMountedRef.current === true && enableTabHaptics === true ) {
            void triggerSelectionHaptic();
        }
    }, [ enableTabHaptics, isControlled, onActiveTabIdChange, tabs ] );

    return {
        activeTabId,
        setActiveTabId,
    };
};
